// Wire mapping + transport helpers shared by the read and run paths.

import { getOntologyServiceBaseUrl } from '../../../config'
import { createOntologyHeaders } from '../../core'
import type { TagClassificationRecord, TagLlmClass, TagLlmRole } from './types'

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function strArr(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
}

export function classificationFromJson(raw: unknown): TagClassificationRecord | null {
  if (raw === null || typeof raw !== 'object') return null
  const j = raw as Record<string, unknown>
  if (
    typeof j.node_id !== 'string' ||
    typeof j.tag_path !== 'string' ||
    typeof j.llm_class !== 'string'
  )
    return null
  return {
    nodeId: j.node_id,
    tagPath: j.tag_path,
    tagClass: j.llm_class as TagLlmClass,
    role: typeof j.llm_role === 'string' ? (j.llm_role as TagLlmRole) : null,
    isPercentage: typeof j.llm_is_percentage === 'boolean' ? j.llm_is_percentage : null,
    expectedRangeLow: num(j.llm_expected_range_low),
    expectedRangeHigh: num(j.llm_expected_range_high),
    goodStates: strArr(j.llm_good_states),
    badStates: strArr(j.llm_bad_states),
    warningStates: strArr(j.llm_warning_states),
    confidence:
      j.llm_confidence === 'high' || j.llm_confidence === 'medium' ? j.llm_confidence : 'low',
    reasoning: typeof j.llm_reasoning === 'string' ? j.llm_reasoning : '',
    model: typeof j.llm_model === 'string' ? j.llm_model : null,
    classifiedAt: typeof j.llm_classified_at === 'string' ? j.llm_classified_at : null
  }
}

export async function sendJson<T>(
  method: 'POST' | 'PUT',
  path: string,
  body: unknown,
  signal?: AbortSignal
): Promise<T> {
  const resp = await fetch(`${getOntologyServiceBaseUrl()}${path}`, {
    method,
    headers: createOntologyHeaders(),
    body: JSON.stringify(body),
    signal
  })
  if (!resp.ok) {
    let message = `${resp.status} ${resp.statusText}`
    try {
      const errBody = (await resp.json()) as { error?: unknown }
      if (typeof errBody.error === 'string') message = errBody.error
    } catch {
      // keep the status-line message
    }
    throw new Error(message)
  }
  return (await resp.json()) as T
}

export async function postJson<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return sendJson<T>('POST', path, body, signal)
}
